﻿/**
 * Screen-2 card in the two-screen preset picker. Renders one level's full
 * field list (built-ins + preset-owned customs) grouped by entity, so the
 * user can compare Minimal / Standard / Detailed side-by-side and pick the
 * one that matches their depth needs.
 *
 * Built-in field keys are mapped to their i18n labels (the same labels the
 * Customize view uses). Required fields get a small * marker; preset-owned
 * customs are tagged "custom" so users see they aren't built-ins.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import type {
  FormEntityKey,
  FormPreset,
  PresetLevel,
} from "../../api/types";

const ENTITY_ORDER: FormEntityKey[] = [
  "customer",
  "supplier",
  "sale",
  "expense",
  "catalog",
  "inventory",
  "employee",
];

interface Props {
  level: PresetLevel;
  preset: FormPreset;
  /** Highlighted when this level is the currently-applied one. */
  active?: boolean;
  onPick: (level: PresetLevel) => void;
}

export function LevelCard({ level, preset, active, onPick }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const levelName = t(`settings.formPresets.levels.${level}` as never) as string;

  // Total field count across all entities (built-ins + customs) for the badge.
  const totalCount = Object.values(preset.entities).reduce((acc, ec) => {
    if (!ec) return acc;
    return acc + ec.builtin_visible.length + ec.customs.length;
  }, 0);

  return (
    <article
      data-testid={`level-card-${level}`}
      data-active={active ? "true" : undefined}
      className={
        "flex flex-col rounded-xl border bg-white dark:bg-slate-800 shadow-sm transition-all " +
        (active
          ? "border-accent-500 ring-1 ring-accent-300 dark:ring-accent-700"
          : "border-slate-200 dark:border-slate-700 hover:border-accent-300 dark:hover:border-accent-600 hover:shadow-md")
      }
    >
      <header className="border-b border-slate-100 dark:border-slate-700/60 px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="text-base font-semibold text-fg-primary">
              {levelName}
            </h4>
            <p className="mt-0.5 text-xs text-fg-secondary">
              {t(`settings.formPresets.levelDescription.${level}` as never)}
            </p>
          </div>
          {active && (
            <span
              data-testid={`level-card-${level}-active-badge`}
              title={t("settings.formPresets.currentLevel") as string}
              className="inline-flex flex-shrink-0 items-center gap-0.5 rounded-full bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-200 px-1.5 py-0.5 text-[10px] font-medium"
            >
              <Check className="h-2.5 w-2.5" strokeWidth={3} />
              {t("settings.formPresets.currentLevel")}
            </span>
          )}
        </div>
        <p className="mt-2 text-[11px] uppercase tracking-wider text-fg-muted">
          {t("settings.formPresets.fieldCount", { count: totalCount })}
        </p>
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          data-testid={`level-card-${level}-toggle`}
          aria-expanded={expanded}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent-700 hover:text-accent-800 dark:text-accent-300 dark:hover:text-accent-200"
        >
          {expanded
            ? t("settings.formPresets.hideFields")
            : t("settings.formPresets.seeFields")}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
      </header>

      {/* Field list grouped by entity — collapsed by default. */}
      {expanded && (
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {ENTITY_ORDER.map((entity) => {
          const cfg = preset.entities[entity];
          if (!cfg) return null;
          const hasBuiltins = cfg.builtin_visible.length > 0;
          const hasCustoms = cfg.customs.length > 0;
          if (!hasBuiltins && !hasCustoms) return null;

          const requiredSet = new Set(cfg.builtin_required);
          const entityLabel = t(`settings.formFields.entity.${entity}` as never) as string;

          return (
            <section key={entity} data-testid={`level-card-${level}-entity-${entity}`}>
              <h5 className="text-[11px] font-semibold uppercase tracking-wider text-fg-secondary mb-1.5">
                {entityLabel}
              </h5>
              <ul className="space-y-1">
                {cfg.builtin_visible.map((key) => {
                  const label = t(
                    `settings.formFields.field.${entity}.${key}` as never,
                    { defaultValue: key },
                  ) as string;
                  const required = requiredSet.has(key);
                  return (
                    <li
                      key={key}
                      className="flex items-baseline gap-1.5 text-fg-primary"
                    >
                      <span className="text-fg-muted" aria-hidden>
                        •
                      </span>
                      <span className="flex-1">{label}</span>
                      {required && (
                        <span
                          title={t("settings.formFields.required") as string}
                          className="text-[10px] font-semibold text-accent-600 dark:text-accent-400"
                        >
                          *
                        </span>
                      )}
                    </li>
                  );
                })}
                {cfg.customs.map((cf) => (
                  <li
                    key={cf.key}
                    className="flex items-baseline gap-1.5 text-fg-primary"
                  >
                    <span className="text-fg-muted" aria-hidden>
                      •
                    </span>
                    <span className="flex-1 italic">{cf.label}</span>
                    <span className="text-[10px] uppercase tracking-wider text-fg-muted">
                      {t("settings.formPresets.customTag")}
                    </span>
                    {cf.is_required && (
                      <span
                        title={t("settings.formFields.required") as string}
                        className="text-[10px] font-semibold text-accent-600 dark:text-accent-400"
                      >
                        *
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      )}

      <footer className="border-t border-slate-100 dark:border-slate-700/60 px-5 py-3">
        <button
          type="button"
          onClick={() => onPick(level)}
          data-testid={`level-card-${level}-pick`}
          className={
            "w-full rounded-lg px-3 py-2 text-sm font-medium transition-colors " +
            (active
              ? "bg-accent-100 text-accent-800 hover:bg-accent-200 dark:bg-accent-900/40 dark:text-accent-200 dark:hover:bg-accent-900/60"
              : "bg-accent-600 text-white hover:bg-accent-700")
          }
        >
          {active
            ? t("settings.formPresets.reapplyLevel", { level: levelName })
            : t("settings.formPresets.pickLevel", { level: levelName })}
        </button>
      </footer>
    </article>
  );
}
